import { memo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import CustomButton from '../../shared/customButton';
import PageContainer from '../../shared/pageContainer';
import { ReactComponent as ErrorPlaceholder } from '../../../assets/images/error.svg';
import { APP_PAGES } from '../../../constants/pages';
import { useTranslations } from '../../../utility/useTranslations';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  mainAuthorized: {
    minHeight: 'calc(100vh - 105px)',
  },
  title: {
    color: primary.pink1,
  },
  description: {
    fontSize: 24,
  },
  button: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish1}`,
    color: primary.bluish1,
    borderRadius: 44,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 14,
    lineHeight: '24px',
    minHeight: 40,
    maxHeight: 40,
    marginTop: spacing(3),
  },
  errorPlaceholder: {
    maxHeight: 'calc(100vh - 334px)',
    width: 'auto',
  },
}));

const PageNotFoundContent = ({ translations, navigate, isAuthorized }) => {
  const classes = useStyles();

  const handleGoHome = () => navigate('/');

  return (
    <div
      className={classNames(classes.main, {
        [classes.mainAuthorized]: isAuthorized,
      })}
    >
      <Typography variant="h1" className={classes.title}>
        {translations.pageTitle}
      </Typography>
      <Typography className={classes.description}>
        {translations.description}
      </Typography>
      <CustomButton
        className={classes.button}
        type="addWithTextRounded"
        onClick={handleGoHome}
      >
        {translations.goHome}
      </CustomButton>
      <ErrorPlaceholder className={classes.errorPlaceholder} />
    </div>
  );
};

PageNotFoundContent.propTypes = {
  translations: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  isAuthorized: PropTypes.bool.isRequired,
};

const PageNotFound = ({ auth, ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const translations = useTranslations(APP_PAGES.NOT_FOUND);
  const isAuthorized = !!(auth && auth?.access);

  const content = (
    <PageNotFoundContent
      translations={translations}
      navigate={navigate}
      isAuthorized={isAuthorized}
    />
  );

  if (isAuthorized) {
    return (
      <PageContainer
        {...rest}
        auth={auth}
        translations={translations}
        location={location}
        navigate={navigate}
        shouldPassProps={false}
        isFullWidthContent
        shouldRemoveSpacing
      >
        {content}
      </PageContainer>
    );
  }

  return content;
};

export default memo(PageNotFound);
